// ABOUTME: Modal component for editing user roles with tag cloud suggestions
// ABOUTME: Handles role input validation, tag cloud display, and permission-based editing

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Chip,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import { roleAPI } from '../services/api';
import { RoleSuggestion } from '../types/api';

interface RoleEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (newRole: string, isPrimaryCaregiver: boolean) => void;
  currentRole: string;
  userId: number;
  familyId: number;
  isAdmin?: boolean;
  isPrimaryCaregiver?: boolean;
}

const RoleEditModal: React.FC<RoleEditModalProps> = ({
  open,
  onClose,
  onSave,
  currentRole,
  isPrimaryCaregiver = false
}) => {
  const [role, setRole] = useState(currentRole);
  const [isPrimaryCaregiverLocal, setIsPrimaryCaregiverLocal] = useState(isPrimaryCaregiver);
  const [roleSuggestions, setRoleSuggestions] = useState<RoleSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Load role suggestions when modal opens
  useEffect(() => {
    if (open) {
      setRole(currentRole);
      setIsPrimaryCaregiverLocal(isPrimaryCaregiver);
      setError(null);
      setValidationError(null);
      loadRoleSuggestions();
    }
  }, [open, currentRole, isPrimaryCaregiver]);

  const loadRoleSuggestions = async () => {
    setLoading(true);
    try {
      const suggestions = await roleAPI.getRoleSuggestions();
      setRoleSuggestions(suggestions);
    } catch {
      setError('Failed to load role suggestions');
    } finally {
      setLoading(false);
    }
  };

  const validateRole = (roleValue: string): string | null => {
    if (!roleValue.trim()) {
      return 'Role is required';
    }
    if (roleValue.trim().length < 2) {
      return 'Role must be at least 2 characters';
    }
    if (roleValue.trim().length > 50) {
      return 'Role must be maximum 50 characters';
    }
    return null;
  };

  const handleSave = async () => {
    const trimmedRole = role.trim();
    const validation = validateRole(trimmedRole);
    
    if (validation) {
      setValidationError(validation);
      return;
    }

    setSaving(true);
    try {
      await onSave(trimmedRole, isPrimaryCaregiverLocal);
      onClose();
    } catch {
      setError('Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleTagClick = (suggestionRole: string) => {
    setRole(suggestionRole);
    setValidationError(null);
  };

  const handleClear = () => {
    setRole('');
    setValidationError(null);
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        Edit Role
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={saving}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3, mt: 1 }}>
          <TextField
            fullWidth
            label="Role"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              setValidationError(null);
            }}
            error={!!validationError}
            helperText={validationError}
            disabled={saving}
            InputProps={{
              endAdornment: role && (
                <IconButton
                  aria-label="clear"
                  onClick={handleClear}
                  edge="end"
                  size="small"
                  disabled={saving}
                >
                  <ClearIcon />
                </IconButton>
              )
            }}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={isPrimaryCaregiverLocal}
              onChange={(e) => setIsPrimaryCaregiverLocal(e.target.checked)}
              disabled={saving || isPrimaryCaregiver} // Disable if already primary caregiver
            />
          }
          label={
            isPrimaryCaregiver 
              ? "Primary Caregiver (Family Owner) - Currently active" 
              : "Primary Caregiver (Family Owner)"
          }
          sx={{ mb: 2 }}
        />
        
        {isPrimaryCaregiver && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
            To transfer primary caregiver status, edit another family member and make them the primary caregiver.
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Suggestions:
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              mb: 2 
            }}>
              {roleSuggestions.map((suggestion) => (
                <Chip
                  key={suggestion.id}
                  label={suggestion.roleName}
                  onClick={() => handleTagClick(suggestion.roleName)}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              ))}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              or add your own
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleEditModal;